import * as Yup from "yup"
import { useFormik } from 'formik'
import { Link } from "react-router-dom"
import { createSelector } from "reselect"
import 'react-toastify/dist/ReactToastify.css'
import { withTranslation } from 'react-i18next'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector, useDispatch } from "react-redux"
import withRouter from '../../../components/Common/withRouter'
import BreadCrumb from '../../../components/Common/BreadCrumb'
import {
  Modal,
  ModalHeader,
  ModalBody,
  Row,
  Col,
  Label,
  Input,
  UncontrolledCollapse
} from 'reactstrap'
import DeleteModal from "../../../components/Global/DeleteModal"
import TableContainer from "../../../components/Common/TableContainer"
import SinpleActionButton from '../../../components/Global/SinpleActionButton'
import ImportModal from './ImportModal'
import AccountModal from './AccountModal'
import {
  getClassesAccounts as onGetClassesAccounts,
  getParentClassesAccount as onGetParentClassesAccount,
  getParentSubClassAccount as onGetParentSubClassAccount,
  getTypesClassesAccount as onGetTypesClassesAccount,
  getClassAccount as onGetClassAccount,
  createClassAccount as onCreateClassAccount,
  updateClassAccount as onUpdateClassAccount,
  deleteClassAccount as onDeleteClassAccount,
  searchClassAccount as onSearchClassAccount
} from "../../../slice/thunks"
import GlobalLoader from "../../../components/Common/GlobalLoader"
import TableContainerBase from "../../../components/Common/TableContainerBase"
import {getAccountTypes} from "../../../helpers/backend_helper"

const Accounting = (props) => {
  const dispatch = useDispatch()

  // Selectors
  const selectLayoutState = (state) => state.Settings
  const SettingsProperties = createSelector(
    selectLayoutState,
    (setting) =>({
      classesAccount: setting.classesAccount,
      parentClassesAccount: setting.parentClassesAccount,
      parentSubClassesAccount: setting.parentSubClassesAccount,
      typeClassesAccount: setting.typeClassesAccount,
      classAccount: setting.classAccount,
      ClassAccountCreateSuccess: setting.ClassAccountCreateSuccess,
      ClassAccountUpdateSuccess: setting.ClassAccountUpdateSuccess,
      ClassAccountDeleteSuccess: setting.ClassAccountDeleteSuccess,
      ClassAccountImportSuccess: setting.ClassAccountImportSuccess,
      error: setting.error
    })
  )

  const {
    classesAccount,
    parentClassesAccount,
    typeClassesAccount,
    classAccount,
    ClassAccountCreateSuccess,
    ClassAccountUpdateSuccess,
    ClassAccountDeleteSuccess,
    ClassAccountImportSuccess,
    parentSubClassesAccount,
    error
  } = useSelector(SettingsProperties)

  // States
  const [modal, setModal] = useState(false)
  const [importModal, setImportModal] = useState(false)
  const [modalParent, setModalParent] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [parentOptions, setParentOptions] = useState([])
  const [typesOptions, setTypesOptions] = useState([])
  const [selectedParent, setSelectedParent] = useState(null)
  const [selectedParentFilter, setSelectedParentFilter] = useState(null)
  const [searchInput, setSearchInput] = useState('')
  const [selectedTypes, setSelectedTypes] = useState(null)
  const [currentClassAccount, setCurrentClassAccount] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [reconciled, setReconciled] = useState(false)

  // Initial data loading
  useEffect(() => {
    loadClassesAccount()
    loadAllParentClassesAccount()
  }, [dispatch])

  // Parent options setup
  useEffect(() => {
    if (parentClassesAccount && parentClassesAccount.length > 0) {
      let parent_list = [{label: " ", value: null}]
      let list = parentClassesAccount.map(classAccount => {
        return { label: classAccount.name, value: classAccount.id }
      })
      setParentOptions([...parent_list, ...list])
    }
  }, [parentClassesAccount])

  // Account types fetching
  const fetchAccountTypes = useCallback(async () => {
    try {
      const {data} = await getAccountTypes()
      setTypesOptions(data.map((item) => (Object.assign(item, {label: item.title}))))
    } catch (e) {
      console.log("error", e)
    }
  }, [])

  useEffect(() => {
    fetchAccountTypes()
  }, [fetchAccountTypes])

  // Success handlers
  useEffect(() => {
    if (ClassAccountCreateSuccess && !error) {
      resetModalStates()
      refreshData()
    }
  }, [ClassAccountCreateSuccess, error])

  useEffect(() => {
    if (ClassAccountUpdateSuccess && !error) {
      resetModalStates()
      refreshData()
    }
  }, [ClassAccountUpdateSuccess, error])

  useEffect(() => {
    if (ClassAccountDeleteSuccess && !error) {
      resetModalStates()
      refreshData()
    }
  }, [ClassAccountDeleteSuccess, error])

  // Loading state
  useEffect(() => {
    if (classesAccount && classesAccount.page) {
      setIsLoading(false)
    }
  }, [classesAccount])

  // Search and filter effects
  useEffect(() => {
    if (selectedParentFilter && selectedParentFilter.value) {
      loadClassesAccount(1, 10)
    }
  }, [selectedParentFilter])

  useEffect(() => {
    if (searchInput.length >= 3) {
      loadClassesAccount(1, 10)
    } else if (searchInput < 1) {
      if (selectedParentFilter && selectedParentFilter.value != null) {
        loadClassesAccount(1, 10)
      } else {
        loadClassesAccount()
      }
    }
  }, [searchInput])

  // Helper functions
  const resetModalStates = () => {
    setIsEdit(false)
    setSelectedParent(null)
    setSelectedTypes(null)
    validation.resetForm()
    setModal(false)
    setCurrentClassAccount(null)
  }

  const refreshData = () => {
    loadClassesAccount()
    loadAllParentClassesAccount()
  }

  // Form validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: (currentClassAccount && currentClassAccount.id) || '',
      code: (currentClassAccount && currentClassAccount.code) || '',
      name: (currentClassAccount && currentClassAccount.name) || '',
      parentClassAccountId: (currentClassAccount && currentClassAccount.parent && currentClassAccount.parent.id) || '',
      classAccountType: (currentClassAccount && currentClassAccount.parent && currentClassAccount.parent.id) || '',
      description: (currentClassAccount && currentClassAccount.description) || '',
      reconciled: (currentClassAccount && currentClassAccount.reconciled) || false,
    },
    validationSchema: Yup.object({
      name: Yup.string().required(props.t("you_need_provide_name")),
      code: Yup.string().required(props.t("you_need_provide_code")),
    }),
    onSubmit: (values) => {
      if (isEdit) {
        const updateClassAccountForm = {
          id: currentClassAccount && currentClassAccount.id,
          name: values.name,
          code: values.code,
          description: values.description,
          accountType: selectedTypes && selectedTypes.value,
          parentClassAccountId: selectedParent && selectedParent.value,
          reconciled: reconciled,
        }
        dispatch(onUpdateClassAccount({ body: updateClassAccountForm, id: currentClassAccount.id }))
      } else {
        const newClassAccountForm = {
          name: values.name,
          code: values.code,
          description: values.description,
          accountType: selectedTypes && selectedTypes.value,
          parentClassAccountId: selectedParent && selectedParent.value || null,
          reconciled: reconciled,
        }
        dispatch(onCreateClassAccount(newClassAccountForm))
      }
    },
  })

  // Data loading functions
  const loadClassesAccount = useCallback((pageNumber, pageSize) => {
    dispatch(onGetClassesAccounts())
  }, [dispatch])

  const loadAllParentClassesAccount = useCallback(() => {
    dispatch(onGetParentClassesAccount())
  }, [dispatch])

  const loadParentSubClassAccount = useCallback((id) => {
    dispatch(onGetParentSubClassAccount(id))
  }, [dispatch])

  // Event handlers
  const toggle = useCallback(() => {
    if (modal) {
      resetModalStates()
    } else {
      setModal(true)
    }
  }, [modal])

  const toggleImport = useCallback(() => {
    setImportModal(!importModal)
  }, [importModal])

  const toggleParent = useCallback(() => {
    setModalParent(!modalParent)
  }, [modalParent])

  const handleOpen = () => {
    setIsEdit(false)
    toggle()
  }

  const handleOpenImport = () => {
    toggleImport()
  }

  const handleImportSuccess = () => {
    refreshData()
  }

  const handleOpenParent = (row) => {
    loadParentSubClassAccount(row.parent.id)
    toggleParent()
  }

  const handleSelectAccount = (arg) => {
    const selectedAccount = arg
    setCurrentClassAccount({
      id: selectedAccount.id,
      name: selectedAccount.name,
      code: selectedAccount.code,
      wards: selectedAccount.wards,
      description: selectedAccount.description
    })
    setReconciled(selectedAccount.reconciled)
    setSelectedParent({
      label: selectedAccount.parent && selectedAccount.parent.name,
      value: selectedAccount.parent && selectedAccount.parent.id
    })
    setSelectedTypes(typesOptions.filter(item => selectedAccount.accountType === item.value).pop())
    setIsEdit(true)
    toggle()
  }

  const handleSearch = () => {
    loadClassesAccount(1, 10)
  }

  const handleSelectParentFilter = (selectedVal) => {
    setSelectedParentFilter(selectedVal)
  }

  const onClickDelete = (classAccount) => {
    setCurrentClassAccount(classAccount)
    setDeleteModal(true)
  }

  const handleDeleteClassAccount = () => {
    if (currentClassAccount) {
      dispatch(onDeleteClassAccount(currentClassAccount.id))
      setDeleteModal(false)
    }
  }

  const handleSelectParent = (selectedParent) => {
    setSelectedParent(selectedParent)
  }

  const handleSelectType = (selectedType) => {
    setSelectedTypes(selectedType)
  }

  const handleCloseAccountModal = () => {
    resetModalStates()
  }

  // Table columns
  const columns = useMemo(
    () => [
      {
        header: props.t("name"),
        accessorKey: "name",
        style: "button",
        enableColumnFilter: false,
      },
      {
        header: props.t("code"),
        accessorKey: "code",
        style: "button",
        enableColumnFilter: false,
      },
      {
        header: props.t("parent"),
        accessorKey: "wards",
        style: "button",
        enableColumnFilter: false,
        cell: (cellProps) => {
          return (
            <Link
              to="#"
              className="text-dark d-inline-block edit-item-btn"
              onClick={() => {
                const classAccount = cellProps.row.original
                handleOpenParent(classAccount)
              }}
            >
              <span>
                {(cellProps.row.original.parent != null) ? cellProps.row.original.parent.name : ''}
              </span>
            </Link>
          )
        }
      },
      {
        header: props.t("action"),
        style: "text-end",
        cell: (cellProps) => {
          return (
            <div className="d-flex justify-content-end">
              <ul className="list-inline hstack mb-0">
                <li className="list-inline-item edit" title="Edit">
                  <Link
                    to="#"
                    className="text-primary d-inline-block edit-item-btn"
                    onClick={() => {
                      const classAccount = cellProps.row.original
                      handleSelectAccount(classAccount)
                    }}
                  >
                    <img src="../assets/images/edit.png" alt="" className="me-2" />
                  </Link>
                </li>
                <li className="list-inline-item" title="Remove">
                  <Link
                    to="#"
                    className="text-danger d-inline-block remove-item-btn"
                    onClick={() => {
                      const classAccount = cellProps.row.original
                      onClickDelete(classAccount)
                    }}
                  >
                    <img src="../assets/images/delete.png" alt="" />
                  </Link>
                </li>
              </ul>
            </div>
          )
        },
      },
    ],
    [handleSelectAccount]
  )

  const columnsParent = useMemo(
    () => [
      {
        header: props.t("name"),
        accessorKey: "name",
        style: "button",
        enableColumnFilter: false,
      },
      {
        header: props.t("code"),
        accessorKey: "code",
        style: "button",
        enableColumnFilter: false,
      },
    ],
    [parentSubClassesAccount]
  )

  // Utility function
  const addParentToSubaccounts = (classAccount) => {
    if (classAccount && classAccount.subAccounts) {
      return classAccount.subAccounts.map((subAccount) => ({
        ...subAccount,
        parent: {
          name: classAccount.name,
          code: classAccount.code,
          id: classAccount.id,
          description: classAccount.description
        }
      }))
    }
    return []
  }

  // Show global loader during import
  const isImportingGlobal = useSelector(state => state.Settings.ClassAccountImportSuccess === undefined && importModal)

  if (isLoading || isImportingGlobal) {
    return <GlobalLoader />
  }

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteClassAccount}
        onCloseClick={() => setDeleteModal(false)}
      />

      <ImportModal
        isOpen={importModal}
        toggle={toggleImport}
        onImportSuccess={handleImportSuccess}
        t={props.t}
      />

      <AccountModal
        isOpen={modal}
        toggle={toggle}
        isEdit={isEdit}
        validation={validation}
        selectedParent={selectedParent}
        selectedTypes={selectedTypes}
        parentOptions={parentOptions}
        typesOptions={typesOptions}
        reconciled={reconciled}
        setReconciled={setReconciled}
        handleSelectParent={handleSelectParent}
        handleSelectType={handleSelectType}
        onClose={handleCloseAccountModal}
        t={props.t}
      />

      <div className="all-beds-setting px-3">
        <div className="patients-list py-4 px-2">
          <div className="container-fluid">
            <div className="row list-patients d-flex justify-content-between">
              <div className="col-lg-5">
                <BreadCrumb
                  title={props.t("classes_account")}
                  pageTitle={props.t("settings")}
                />
              </div>
              <div className="col-sm-auto">
                <div className="d-flex gap-2">
                  <SinpleActionButton
                    title={props.t("create_class_account")}
                    img="../assets/images/user.png"
                    action={handleOpen}
                  />
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={handleOpenImport}
                  >
                    <i className="fas fa-upload me-2"></i>
                    {props.t("import") || "Importer"}
                  </button>
                </div>
              </div>
            </div>

            <div className="payment-mode-lists">
              <div className="card container">
                <Row className="mt-3">
                  <Col xl={4} sm={4}>
                    <div className='mb-2'>
                      <Label htmlFor="status-field" className="form-label">
                        {props.t("search_class_account")}
                      </Label><br/>
                      <div className="d-flex align-items-center">
                        <div className="search-box me-2 mb-1 col-10">
                          <Input
                            className="custom-border form-control"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={handleSearch}
                          className="btn btn-primary"
                        >
                          <i className="fa fa-search p-0"></i>
                        </button>
                      </div>
                    </div>
                  </Col>
                </Row>

                <Row className="mx-3">
                  {classesAccount && classesAccount.map((classAccount, key) => (
                    <React.Fragment key={key}>
                      <div
                        id={String(`toggle-${key}`)}
                        className="col-12 border border-1 rounded-3 p-2 mb-2 d-flex justify-content-between"
                      >
                        <div>{classAccount.name} : {classAccount.subAccounts.length} items</div>
                        <div><i className="fa fa-pencil"></i></div>
                      </div>
                      <UncontrolledCollapse toggler={String(`toggle-${key}`)}>
                        <div className="row justify-content-between">
                          <div className="mb-3">
                            <TableContainer
                              columns={columns}
                              data={addParentToSubaccounts(classAccount)}
                              isGlobalFilter={false}
                              customPageSize={classAccount.subAccounts.length}
                              divClass="table-card-ward"
                              tableClass="align-middle"
                              theadClass="thead-light table-light"
                              SearchPlaceholder={props.t("search_class_account")}
                              showPagination={false}
                            />
                          </div>
                        </div>
                      </UncontrolledCollapse>
                    </React.Fragment>
                  ))}
                </Row>
              </div>
            </div>

            {/* Parent Modal */}
            <Modal
              id='parentModal'
              size='lg'
              isOpen={modalParent}
              toggle={toggleParent}
              backdrop={'static'}
              centered
            >
              <ModalHeader className="bg-light p-3" toggle={toggleParent}>
                {props.t("all_classes_account")}
              </ModalHeader>
              <ModalBody>
                <div className="row justify-content-between">
                  <div className="card table-card" style={{height: "50vh"}}>
                    <TableContainerBase
                      columns={columnsParent}
                      data={((parentSubClassesAccount && parentSubClassesAccount) || [])}
                      isGlobalFilter={false}
                      isAddUserList={false}
                      customPageSize={100}
                      divClass="card table-card table-responsive p-0"
                      tableClass="table align-middle"
                      theadClass="table-light text-muted"
                      handlePlanClick={loadParentSubClassAccount}
                      isCustomerFilter={false}
                      SearchPlaceholder={props.t("")}
                    />
                  </div>
                </div>
              </ModalBody>
              <div className='p-3'>
                <Row className="align-items-center">
                  <div className="col-sm"></div>
                  <div className="col-sm-auto">
                    <div className="hstack gap-2 justify-content-end">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setModalParent(false)}
                      >
                        {props.t("close")}
                      </button>
                    </div>
                  </div>
                </Row>
              </div>
            </Modal>
          </div>
        </div>
      </div>
    </React.Fragment>
  )
}

export default withRouter(withTranslation()(Accounting))